import { useRef } from 'react'

import { faGear } from '@fortawesome/pro-light-svg-icons/faGear'
import { faPenLine } from '@fortawesome/pro-light-svg-icons/faPenLine'
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'

import { faArrowDownToBracket } from '@fortawesome/pro-regular-svg-icons/faArrowDownToBracket'

import { useDecision } from '@optimizely/react-sdk'
import Icon from '@space-core/components/icon'
import { Actions, ComponentNames, Entities, EventNames } from '@space-core/datahub'

import { DataHubEntities, DataHubService } from '@space-core/datahub/hotmartClub'
import { useDeviceInfo } from '@space-core/hooks/useDeviceInfo'
import { modalEventDispatch } from '@space-events/modal'
import { toggleCommunityConfigurationSidebarEventDispatch } from '@space-events/toggleCommunityConfigurationSidebar'
import Collapse from '@space-shell/common/components/newCollapse'

import useMembership from '@space-shell/common/hooks/useMembership'
import { useUserPermissions } from '@space-shell/common/hooks/useUserPermissions'
import type { CommunityProps } from '@space-shell/common/services/communities'
import { PermissionActions } from '@space-shell/common/services/communities'
import { CommunitiesActions } from '@space-shell/common/services/communities'
import { UserInfoRole } from '@space-shell/common/services/user'
import { RoutesPaths } from '@space-shell/core/router'
import { RoleType, useUserInfoStore, useViewAsStore } from '@space-shell/core/store'
import { COMMUNITY_TYPE } from '@space-shell/modules/communities/accessAndPrivacy/constants'
import type {
  CommunityModalEvent,
  CommunityModalType
} from '@space-shell/modules/communities/communityModal'
import {
  CommunityEventType,
  useCommunityModal
} from '@space-shell/modules/communities/communityModal'

import useDeleteCommunityModal from '@space-shell/modules/communities/deleteCommunityModal/hooks/useDeleteCommunityModal/useDeleteCommunityModal'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'
import ClickToCreateCommunitySchema from '@space-shell/modules/home/datahub/schemas/click/community/clickToCreateCommunitySchema'
import ClickToGoToMigratorSchema from '@space-shell/modules/home/datahub/schemas/click/community/clickToGoToMigratorSchema'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CollapseHeader from './collapseHeader'
import CollapseItemActions from './collapseItemActions'

import { CommunitiesListName } from './communitiesListName'
import CommunityBadge from './communityBadge'
import { CollapseItemBox, CommunitiesListAvatarBox, CustomButton } from './styles'
import type { MenuCollapseProps } from './types'

import { CommunitiesListAvatar } from '../communitiesListAvatar'
import { CommunityListBadge } from '../communityListBadge'
import useScreenName from '../hooks/useScreenName'

const MenuCollapse = ({ communities, isActive, redirectCommunity, group }: MenuCollapseProps) => {
  const { t } = useTranslation()
  const user = useUserInfoStore()
  const screenName = useScreenName()
  const { membershipSlug } = useMembership()
  const { role: viewAsRole } = useViewAsStore()
  const collapseRef = useRef<HTMLDivElement>(null)
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { isOpen: isCommunityModalOpen } = useCommunityModal()
  const deleteCommunityModalContext = useDeleteCommunityModal()
  const deviceInfo = useDeviceInfo()
  const [{ enabled: isEditCommunityOptionEnabled }] = useDecision(
    'com_adm_web_enable_edit_community_option'
  )
  const {
    i18n: { language }
  } = useTranslation()
  const navigate = useNavigate()

  const isOwner = user?.role === UserInfoRole.OWNER

  const { hasPermission } = useUserPermissions()

  const [{ enabled: isEnabledImportCommunity }] = useDecision(
    'com_adm_web_enable_import_community_button'
  )

  const toggleCommunityModal = (actionType: CommunitiesActions, communityId?: number) => {
    modalEventDispatch<CommunityModalEvent>({
      eventType: isCommunityModalOpen ? CommunityEventType.CLOSE : CommunityEventType.OPEN,
      modalType: actionType as CommunityModalType,
      groupId: group?.id,
      communityId
    })
  }

  const handleClickOpenSettings = (community: CommunityProps) => {
    sendDataHubConsume({
      entity: Entities.COMMUNITY,
      action: Actions.SETUP,
      event: {
        screenName,
        eventName: EventNames.SETUP_COMMUNITY,
        componentName: ComponentNames.SIDE_DROPDOWN,
        communityId: community.id,
        communityName: community.name
      }
    })

    toggleCommunityConfigurationSidebarEventDispatch({ action: 'open', community })
  }

  const collapseItemActions = (community: CommunityProps) => {
    const shouldShowSettingsMenu = [
      PermissionActions.MEMBER_ROLE_READ,
      PermissionActions.ACCESS_AND_PRIVACY,
      PermissionActions.BLOCK_READ
    ].some((permission) => hasPermission(permission, community?.permissions?.actions))

    return [
      {
        callBack: () => handleClickOpenSettings(community),
        label: t('configuration_sidebar.title'),
        icon: faGear,
        isVisibleItem: shouldShowSettingsMenu
      },
      {
        callBack: () => toggleCommunityModal(CommunitiesActions.EDIT, community.id),
        label: t('buttons.edit_community'),
        icon: faPenLine,
        isVisibleItem:
          hasPermission(PermissionActions.COLLECTION_UPDATE, community?.permissions?.actions) &&
          isEditCommunityOptionEnabled
      },
      {
        callBack: () => {
          deleteCommunityModalContext.handleDelete(community.id, community.name)
        },
        label: t('buttons.remove_community'),
        icon: faTrash,
        isVisibleItem:
          hasPermission(PermissionActions.COLLECTION_DELETE, community?.permissions?.actions) &&
          community?.type !== COMMUNITY_TYPE.PAID
      }
    ]
  }

  const handleAddCommunity = () => {
    DataHubService.sendEvent({
      entity: DataHubEntities.Community,
      schema: new ClickToCreateCommunitySchema({
        deviceInfo,
        userRole: user.role,
        componentName: ComponentNames.SIDE_MENU
      })
    })

    toggleCommunityModal(CommunitiesActions.ADD)
  }

  const handleImportCommunity = () => {
    DataHubService.sendEvent({
      entity: DataHubEntities.Community,
      schema: new ClickToGoToMigratorSchema({
        deviceInfo,
        userRole: user.role,
        componentName: ComponentNames.CONSUMER_VIEW_BUTTON
      })
    })

    navigate(
      `/${language}/club/${membershipSlug}${RoutesPaths.IMPORT_COMMUNITY}?migrationOrigin=${ComponentNames.CONSUMER_VIEW_BUTTON}`
    )
  }

  if (isOwner && viewAsRole === RoleType.UserLogout) return null

  const shouldRenderActions = isOwner && viewAsRole === RoleType.Creator

  const shouldRenderImportCommunity =
    isOwner && viewAsRole === RoleType.Creator && isEnabledImportCommunity

  return (
    <Collapse>
      <Collapse.CollapseItem ref={collapseRef} className={clsx('_border-top')} active>
        <div slot='title' className='_text-gray-900 _w-full'>
          <CollapseHeader
            hasActions={shouldRenderActions}
            group={group}
            handleAddCommunity={handleAddCommunity}
          />
        </div>

        {shouldRenderImportCommunity && (
          <div slot='content' className='_text-gray-900 '>
            <button
              onClick={handleImportCommunity}
              className='_d-flex _p-0 _align-items-center _w-full hot-button _border-0 _text-1 _text-gray-900'>
              <span className='_d-flex _py-4 _pl-4 _pr-0'>
                <Icon icon={faArrowDownToBracket} className='_text-gray-900 _w-5' />
              </span>

              <span className='_text-gray-600 _font-weight'>{t('buttons.import_community')}</span>
            </button>
          </div>
        )}

        {communities.map((community) => (
          <div slot='content' key={community.id}>
            <CollapseItemBox
              className={clsx('_w-full _d-flex _align-items-center _rounded', {
                '_font-weight-bold': isActive(community.id)
              })}
              isActive={isActive(community.id)}>
              <CommunitiesListAvatarBox
                className='_rounded _p-4 _d-flex _align-items-center _justify-content-center'
                isActive={isActive(community.id)}>
                <CommunitiesListAvatar
                  showBadge={community.hasUnseenNewsOrCommentReplies}
                  community={community}
                />
              </CommunitiesListAvatarBox>
              <CustomButton
                className={clsx(
                  '_d-flex _py-3 _align-items-center _justify-content-start _bg-transparent _border-0 _rounded _cursor-pointer _text-gray-900 _p-0'
                )}
                onClick={() => {
                  redirectCommunity(community.id, community.name, community.type)
                }}>
                <CommunitiesListName
                  label={community.name}
                  className='_mr-2 _ml-4'
                  hasUnseenNewsOrCommentReplies={community.hasUnseenNewsOrCommentReplies}
                />
              </CustomButton>
              <div className='_ml-auto _d-flex _align-items-center _pr-3'>
                <CommunityBadge type={community.type} />
                {community.hasUnseenNewsOrCommentReplies ? (
                  <CommunityListBadge className='_m-2' />
                ) : null}
                {shouldRenderActions ? (
                  <CollapseItemActions actions={collapseItemActions(community)} />
                ) : null}
              </div>
            </CollapseItemBox>
          </div>
        ))}

        {shouldRenderActions ? (
          <div slot='content'>
            <button
              className='_d-flex _p-0 _align-items-center _w-full hot-button _border-0 _text-1 _text-gray-900'
              onClick={handleAddCommunity}>
              <span className='_d-flex _py-4 _pl-4 _pr-0'>
                <Icon icon={faPlus} className='_text-gray-900 _w-5' />
              </span>
              <span>{t('modal.create_community.save_button')}</span>
            </button>
          </div>
        ) : null}
      </Collapse.CollapseItem>
    </Collapse>
  )
}

export default MenuCollapse
